/** Editor Stability P0 (VistoeraTASK-00103) — the exact "safe to touch
 * pixel data" predicate every canvas-pixel-touching operation in
 * `DualPaneCutoutEditor.tsx` (and any other caller of the shared editor
 * core, e.g. `ChainEditorModal.tsx`) must check before calling
 * `getImageData()` / `putImageData()` / `createImageData()` on
 * `imageCanvasRef`/`maskCanvasRef`.
 *
 * ROOT CAUSE this exists to fix — forensic finding, VistoeraTASK-00103:
 * `CanvasRenderingContext2D.getImageData()` throws a DOM `IndexSizeError`
 * ("The source width/height is 0") whenever EITHER the width or height
 * argument is zero. This is WHATWG HTML spec behavior, not a Vistoera
 * bug — see https://html.spec.whatwg.org/#dom-context-2d-getimagedata.
 * Both `imageCanvasRef`/`maskCanvasRef` are eagerly-created OFFSCREEN
 * canvases (`useRef(document.createElement('canvas'))` — never `null`,
 * so a `!ref.current` check can never catch this) that default to a
 * real `width`/`height` of `0` until the async source-load effect
 * assigns the decoded image's actual dimensions.
 *
 * Before this task, several call sites read/wrote pixel data
 * unconditionally with no readiness guard at all — most directly, the
 * Shell's always-enabled "反选" (Invert) toolbar button
 * (`invertSelection()`) and the left-pane pointer handlers for the
 * "AI 点选"/brush/rect/lasso tools (`pushHistory()` on pointer down/up).
 * Any one of those reached before the source image finished loading
 * (a plausible, ordinary interaction — not a contrived edge case — on a
 * large image or a slow connection, which is exactly why the bug read as
 * "intermittent") threw this uncaught, synchronously, inside a raw React
 * event handler. With no local Image Editor error boundary anywhere
 * above it at the time, that exception bubbled all the way up to the
 * root `CanvasErrorBoundary`, generating a `canvas-xxxxxxx-xxxxxx`
 * reference id and taking down the ENTIRE Canvas Surface — exactly the
 * "画布加载失败" symptom this task was filed to root-cause.
 *
 * `pushHistory`/`invertSelection`/`exportMaskBlob`/`exportEditedImageBlob`
 * in `DualPaneCutoutEditor.tsx` all call this before touching pixel data;
 * `onLeftPointerDown` also calls it once, up front, before dispatching to
 * any tool-specific branch, so a stray interaction during the (network
 * fetch + decode) window is a clean no-op instead of a crash.
 */
export function canvasPixelOperationAllowed(width: number, height: number): boolean {
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
}

/** VistoeraTASK-00412 — the sibling guard to `canvasPixelOperationAllowed`
 * above, for a DIFFERENT (and previously completely unguarded) failure
 * mode: `HTMLCanvasElement.getContext('2d')` returning `null`.
 *
 * ROOT CAUSE this exists to fix — forensic finding, VistoeraTASK-00412:
 * every one of this editor's ~30 call sites did `canvas.getContext('2d')!`
 * — a non-null ASSERTION, never an actual check. Per spec, `getContext`
 * legitimately returns `null` when the browser cannot allocate a new 2D
 * rendering context (most commonly: the UA's own per-page/per-process
 * canvas-context budget has been exhausted — Chromium silently starts
 * returning `null` for new contexts once a page accumulates "too many"
 * live canvases; a Canvas Surface with 100+ nodes, several of which are
 * themselves backed by `<canvas>` elements (zoom/lightbox/this very
 * editor opened+closed repeatedly across a long session), is exactly the
 * realistic environment where this budget gets exercised — not a
 * contrived edge case).
 *
 * The two call sites this task found and fixed (`DualPaneCutoutEditor`'s
 * `initCanvasesTo`/`paintImageTier`/`paintMaskFromImage`/
 * `paintMaskFullyOpaque` — all part of automatic editor-open
 * initialization, never a user-triggered tool action) ran this
 * unconditional assertion SYNCHRONOUSLY inside a `useEffect` body (the
 * "no existing mask, default fully-opaque" branch — the common path for
 * editing a plain, not-yet-cutout image asset). A `null` context there
 * threw a raw, unclassified `TypeError: Cannot read properties of null
 * (reading 'clearRect')` straight out of React's effect-commit phase —
 * which, unlike every other load-failure path in this editor (all
 * already routed through `onSourceError`/the graceful inline "重试" UI),
 * is NOT inside any `async`/`.catch()` boundary, so it propagated
 * uncaught all the way to `ImageEditorErrorBoundary` — the Canvas-fatal-
 * crash-prevention boundary itself, not a controlled failure state —
 * producing exactly the reported "图片编辑器加载失败，请重试" `imgedit-*`
 * crash (two buttons only — correctly NOT the stale-chunk/
 * `isDynamicImportChunkFailure` kind, since this is a genuine runtime
 * exception, not a module-loading one).
 *
 * Fix: a clear, intentional, classifiable `Error` instead of a bare
 * native `TypeError` when context creation fails — callers catch this
 * exactly like any other terminal source/mask-init failure and route it
 * through the SAME existing `onSourceError` graceful-degradation path
 * (never a second, new error-handling mechanism), so the user sees the
 * correct "图片加载失败，请重试" inline state (clicking 重试 re-runs the
 * whole init, which has a real chance of succeeding if the budget
 * pressure was transient — e.g. an unrelated canvas elsewhere on the page
 * having since been garbage-collected) instead of the heavy, whole-
 * editor-destroying crash boundary. */
export function require2dContext(canvas: HTMLCanvasElement, label: string): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error(`canvas 2d context unavailable (${label})`)
  }
  return ctx
}
